/**
 * console-api 客户端。
 *
 * ## 刷新令牌必须串行（这是本文件最重要的一件事）
 *
 * 后端的 refresh 是**一次一换**，并且把「旧 refresh 再次出现」判定为令牌被复制，
 * 会当场销毁整条会话（见 console-api `routes/auth.ts` 的 refresh 分支）。
 *
 * 控制台的一个页面经常并发发五六个请求。access 一过期它们会**同时**拿到 401，
 * 如果每个都各自去 refresh，第一个换走了令牌，后面几个带着已作废的旧令牌撞上重放检测
 * —— 用户会在正常使用中被莫名其妙踢下线，而且必现。
 *
 * 所以这里用一个模块级的 `refreshInFlight`：第一个触发刷新的请求去换，
 * 其余的 await 同一个 promise，拿到新 access 后各自重试一次。
 *
 * ## 只重试一次
 *
 * 刷新后重试仍然 401，说明不是「令牌过期」而是「会话没了」（被踢、改密、移出组织）。
 * 再重试只会打出一串注定失败的请求，直接登出。
 */

export interface ApiErrorBody {
  readonly code: string;
  readonly message: string;
  readonly details?: unknown;
  readonly request_id?: string;
  readonly retryable?: boolean;
}

/**
 * 后端的错误对象。`request_id` 一定要在界面上露出来 ——
 * 用户报障时它是唯一能把前端现象和后端日志对上的东西。
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | null;
  readonly retryable: boolean;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details ?? null;
    this.requestId = body.request_id ?? null;
    this.retryable = body.retryable ?? false;
  }
}

export interface Tokens {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly orgId: string;
}

const STORAGE_KEY = 'bipay.console.session';

let tokens: Tokens | null = readStored();
let onUnauthorized: (() => void) | null = null;
let refreshInFlight: Promise<string> | null = null;

function readStored(): Tokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' && parsed !== null &&
      typeof (parsed as Tokens).accessToken === 'string' &&
      typeof (parsed as Tokens).refreshToken === 'string'
    ) {
      return parsed as Tokens;
    }
  } catch {
    // 存储损坏等于没登录，不是错误
  }
  return null;
}

export function getTokens(): Tokens | null {
  return tokens;
}

export function setTokens(next: Tokens | null): void {
  tokens = next;
  if (next === null) {
    localStorage.removeItem(STORAGE_KEY);
  } else {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  }
}

/** 会话彻底失效时的回调（由 App 挂上去，用来跳回登录页）。 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler;
}

function logout(): void {
  setTokens(null);
  refreshInFlight = null;
  onUnauthorized?.();
}

/** 换一对新令牌。全局只允许有一个在飞。 */
async function refreshAccessToken(): Promise<string> {
  if (refreshInFlight !== null) return await refreshInFlight;

  const current = tokens;
  if (current === null) {
    logout();
    throw new ApiError(401, { code: 'UNAUTHORIZED', message: '未登录' });
  }

  refreshInFlight = (async () => {
    // 这一条不走 buildUrl（它是裸 fetch，不带令牌），前缀要自己拼
    const response = await fetch(`${API_BASE}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refresh_token: current.refreshToken }),
    });
    if (!response.ok) {
      logout();
      throw new ApiError(401, { code: 'UNAUTHORIZED', message: '会话已失效，请重新登录' });
    }
    const body = (await response.json()) as {
      access_token: string;
      refresh_token: string;
      org_id: string;
    };
    setTokens({
      accessToken: body.access_token,
      refreshToken: body.refresh_token,
      orgId: body.org_id,
    });
    return body.access_token;
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  readonly body?: unknown;
  readonly query?: Record<string, string | number | null | undefined>;
  /** 幂等键。写接口里只有创建提现单强制要求，但传了总没坏处。 */
  readonly idempotencyKey?: string;
  /** 登录、刷新这类不带令牌的请求。 */
  readonly anonymous?: boolean;
}

/**
 * 控制台 API 的路径前缀，与 console-api 的 `CONSOLE_BASE` 常量一一对应。
 *
 * 网关对外的商户 API 也挂在 `/v1` 下，且 payments / refunds / disputes 三个名字两边都有。
 * 同域名部署时 `/v1/payments` 会变成二义地址，所以控制台整体挪到 `/console` 下。
 * 移动的是控制台而不是网关：`/v1` 是写进 OpenAPI、要给商户长期依赖的公开契约。
 *
 * 各页面调用处继续写 `/v1/...`，前缀只在这里拼一次。
 */
const API_BASE = '/console';

function buildUrl(path: string, query: RequestOptions['query']): string {
  const full = `${API_BASE}${path}`;
  if (query === undefined) return full;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === null || value === undefined || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs === '' ? full : `${full}?${qs}`;
}

async function parseError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody = {
    code: 'UNKNOWN',
    message: `请求失败（HTTP ${response.status}）`,
  };
  try {
    const parsed = (await response.json()) as { error?: ApiErrorBody };
    if (parsed.error !== undefined) body = parsed.error;
  } catch {
    // 非 JSON 响应（网关 502 之类）保持默认文案
  }
  return new ApiError(response.status, body);
}

async function send(path: string, options: RequestOptions, accessToken: string | null): Promise<Response> {
  const headers: Record<string, string> = {};
  if (options.body !== undefined) headers['content-type'] = 'application/json';
  if (accessToken !== null) headers['authorization'] = `Bearer ${accessToken}`;
  if (options.idempotencyKey !== undefined) headers['idempotency-key'] = options.idempotencyKey;

  return await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
  });
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if (options.anonymous === true) {
    const response = await send(path, options, null);
    if (!response.ok) throw await parseError(response);
    return (await response.json()) as T;
  }

  const current = tokens;
  if (current === null) {
    logout();
    throw new ApiError(401, { code: 'UNAUTHORIZED', message: '未登录' });
  }

  let response = await send(path, options, current.accessToken);

  if (response.status === 401) {
    // 只重试一次：刷新后还是 401 说明会话真的没了，不是令牌过期
    const fresh = await refreshAccessToken();
    response = await send(path, options, fresh);
    if (response.status === 401) {
      logout();
      throw await parseError(response);
    }
  }

  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** 列表响应的统一形状（游标分页）。 */
export interface ListResponse<T> {
  readonly data: readonly T[];
  readonly next_cursor: string | null;
}

/** 不分页的集合响应（通道、授权、费率、成员这些量小的接口用它）。 */
export interface Collection<T> {
  readonly data: readonly T[];
}

/** 幂等键：创建提现单必须带，重复提交不会开出第二张单。 */
export function newIdempotencyKey(): string {
  return crypto.randomUUID();
}
